import { useState, useEffect, useRef } from 'react'
import { useParams, Link, useLocation } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getElectricalPanels, createElectricalPanel, updateElectricalPanel, deleteElectricalPanel } from '../api/electricalPanels'
import { getSchemas, deleteSchema, updateSchema } from '../api/schemas'
import { getSchemaTypes } from '../api/schemaTypes'
import { ElectricalPanel, Schema } from '../types'
import CreateSchemaModal from './CreateSchemaModal'
import { useAuth } from '../AuthContext'
import { PermissionRow } from '../api/admin'
import { apiClient } from '../api/client'

interface ElectricalPanelModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: { proj_id: string; name: string; description: string; location: string }) => void
  panel?: ElectricalPanel
  isLoading: boolean
}

interface EditSchemaModalProps {
  isOpen: boolean
  onClose: () => void
  projectId: number
  panelId: number
  schema: Schema | null
}

function ElectricalPanelModal({ isOpen, onClose, onSubmit, panel, isLoading }: ElectricalPanelModalProps) {
  const [formData, setFormData] = useState({
    proj_id: '',
    name: '',
    description: '',
    location: ''
  })

  // Update form data when panel changes
  useEffect(() => {
    if (panel) {
      setFormData({
        proj_id: panel.proj_id || '',
        name: panel.name || '',
        description: panel.description || '',
        location: panel.location || ''
      })
    } else {
      setFormData({
        proj_id: '',
        name: '',
        description: '',
        location: ''
      })
    }
  }, [panel])

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEsc)
      return () => document.removeEventListener('keydown', handleEsc)
    }
  }, [isOpen, onClose])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-bold mb-4">
          {panel ? 'Редактировать щит' : 'Создать щит'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Код щита
            </label>
            <input
              type="text"
              value={formData.proj_id}
              onChange={(e) => setFormData({ ...formData, proj_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="ЩО1, EP1, ЩР2..."
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Название
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Главный щиток"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Описание
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Описание назначения щита"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Расположение
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Тех. помещение, этаж 1"
            />
          </div>
          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? 'Сохранение...' : (panel ? 'Обновить' : 'Создать')}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400"
            >
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function EditSchemaModal({ isOpen, onClose, projectId, panelId, schema }: EditSchemaModalProps) {
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    schemaTypeId: 1,
  })

  useEffect(() => {
    if (!isOpen || !schema) return
    setFormData({
      name: schema.name || '',
      description: schema.description || '',
      schemaTypeId: schema.schema_type_id || 1,
    })
  }, [isOpen, schema])

  const { data: schemaTypes = [], isLoading: typesLoading } = useQuery({
    queryKey: ['schema-types'],
    queryFn: getSchemaTypes,
    enabled: isOpen,
  })

  const updateSchemaMutation = useMutation({
    mutationFn: ({ id, name, description, schemaTypeId }: { id: number; name: string; description: string; schemaTypeId: number }) =>
      updateSchema(id, {
        name,
        description,
        schema_type_id: schemaTypeId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schemas', String(projectId), panelId] })
      queryClient.invalidateQueries({ queryKey: ['schemas', String(projectId)] })
      queryClient.invalidateQueries({ queryKey: ['schemas'] })
      onClose()
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!schema) return
    const trimmedName = formData.name.trim()
    if (!trimmedName) return

    updateSchemaMutation.mutate({
      id: schema.id,
      name: trimmedName,
      description: formData.description.trim(),
      schemaTypeId: formData.schemaTypeId,
    })
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!isOpen || !schema) return null

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-xl font-bold mb-4">Редактировать схему</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Тип схемы
            </label>
            {typesLoading ? (
              <div className="text-center py-4 text-gray-500">Загрузка типов...</div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {schemaTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, schemaTypeId: type.id })}
                    className={`p-3 text-left rounded-lg border-2 transition-all ${
                      formData.schemaTypeId === type.id
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium text-sm">{type.name}</div>
                    {type.description && (
                      <div className="text-xs text-gray-500 mt-1">{type.description}</div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Название схемы
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Введите название схемы"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Описание
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Описание схемы (необязательно)"
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              disabled={updateSchemaMutation.isPending || typesLoading}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {updateSchemaMutation.isPending ? 'Сохранение...' : 'Сохранить'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400"
            >
              Отмена
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Компонент для отображения схем отдельного щита
function PanelSchemas({ 
  projectId, 
  panelId, 
  onCreateSchema, 
  onEditSchema,
  canEdit, 
  canManage 
}: { 
  projectId: string
  panelId: number
  onCreateSchema: () => void
  onEditSchema: (schema: Schema) => void
  canEdit: boolean
  canManage: boolean
}) {
  const queryClient = useQueryClient()
  const { data: schemas, isLoading } = useQuery({
    queryKey: ['schemas', projectId, panelId],
    queryFn: () => getSchemas(Number(projectId), panelId),
    enabled: !!projectId && !!panelId,
  })

  const { data: schemaTypes = [] } = useQuery({
    queryKey: ['schema-types'],
    queryFn: getSchemaTypes,
  })

  // Мутация для удаления схемы
  const deleteSchemaMutation = useMutation({
    mutationFn: deleteSchema,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schemas', projectId, panelId] })
      queryClient.invalidateQueries({ queryKey: ['schemas', projectId] })
      queryClient.invalidateQueries({ queryKey: ['schemas'] })
    },
  })

  // Функция для удаления схемы с подтверждением
  const handleDeleteSchema = (schemaId: number, schemaName: string) => {
    if (confirm(`Вы уверены, что хотите удалить схему "${schemaName}"?`)) {
      deleteSchemaMutation.mutate(schemaId)
    }
  }

  if (isLoading) {
    return <div className="text-center py-4 text-gray-500">Загрузка схем...</div>
  }

  // Фильтруем схемы для конкретного щита, исключая терминальные схемы (schema_type_id = 6)
  const panelSchemas = (schemas || []).filter(schema => 
    schema.electrical_panel_id === panelId && schema.schema_type_id !== 6
  )

  return (
    <div className="space-y-4">
      {/* Заголовок с кнопкой добавления */}
      <div className="flex justify-between items-center">
        <h4 className="text-lg font-medium text-gray-800">Схемы щита</h4>
        {canEdit && (
          <button
            onClick={onCreateSchema}
            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
          >
            Добавить схему
          </button>
        )}
      </div>

      {/* Схемы */}
      {panelSchemas.length === 0 ? (
        <div className="text-center py-6 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
          <p className="mb-2">Нет схем для этого щита</p>
          {canEdit && (
            <button
              onClick={onCreateSchema}
              className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 inline-block"
            >
              Создать схему
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {panelSchemas.map((schema) => {
            const schemaType = schemaTypes.find((type) => type.id === schema.schema_type_id)
            const isKnxSchema = schemaType?.name === 'KNX'

            return (
              <div key={schema.id} id={`schema-${schema.id}`} className="border border-gray-200 rounded-lg p-3 hover:shadow-sm transition-shadow group relative">
                {/* Кнопки действий */}
                {canEdit && (
                  <div className="absolute top-2 right-2 flex gap-1">
                    {isKnxSchema && (
                      <Link
                        to={`/projects/${projectId}/schemas/${schema.id}/knx`}
                        className="text-gray-600 hover:text-gray-800 p-1 rounded hover:bg-gray-100"
                        title="KNX settings"
                      >
                        ⚙️
                      </Link>
                    )}
                    {canManage && (
                      <button
                        onClick={() => handleDeleteSchema(schema.id, schema.name)}
                        disabled={deleteSchemaMutation.isPending}
                        className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                        title="Удалить схему"
                      >
                        🗑️
                      </button>
                    )}
                    <button
                      onClick={() => onEditSchema(schema)}
                      className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                      title="Редактировать схему"
                    >
                      ✏️
                    </button>
                  </div>
                )}
                
                <div className="mb-2">
                  <h5 className="font-medium text-gray-900 text-sm truncate">{schema.name}</h5>
                </div>
                {schema.description && (
                  <p className="text-gray-600 text-xs mb-2 line-clamp-2">{schema.description}</p>
                )}
                <div className="flex space-x-1">
                  <Link
                    to={`/projects/${projectId}/schemas/${schema.id}`}
                    className="bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700"
                  >
                    Редактор
                  </Link>
                  <Link
                    to={`/projects/${projectId}/schemas/${schema.id}/specification`}
                    className="bg-gray-600 text-white px-2 py-1 rounded text-xs hover:bg-gray-700"
                  >
                    Спец
                  </Link>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default function ElectricalPanels() {
  const { projectId } = useParams<{ projectId: string }>()
  const { user: currentUser } = useAuth()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isCreateSchemaModalOpen, setIsCreateSchemaModalOpen] = useState(false)
  const [isEditSchemaModalOpen, setIsEditSchemaModalOpen] = useState(false)
  const [selectedPanelId, setSelectedPanelId] = useState<number | null>(null)
  const [editingSchema, setEditingSchema] = useState<Schema | null>(null)
  const [editingPanel, setEditingPanel] = useState<ElectricalPanel | undefined>()
  const [openMenuId, setOpenMenuId] = useState<number | null>(null)
  const menuRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const buttonRefs = useRef<Record<number, HTMLButtonElement | null>>({})
  const queryClient = useQueryClient()
  const location = useLocation()

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const focusId = searchParams.get('focus')
    if (focusId) {
      let attempts = 0
      const interval = setInterval(() => {
        const el = document.getElementById(focusId)
        if (el) {
          clearInterval(interval)
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          el.classList.add('ring-4', 'ring-blue-400', 'ring-opacity-50', 'transition-all', 'duration-500')
          setTimeout(() => {
            el.classList.remove('ring-4', 'ring-blue-400', 'ring-opacity-50')
          }, 3000)
        }
        attempts++
        if (attempts > 50) clearInterval(interval)
      }, 100)
      return () => clearInterval(interval)
    }
  }, [location.search])

  // Проверка прав пользователя на редактирование проекта
  const { data: projectAccessData } = useQuery({
    queryKey: ['my-project-access', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) return { access: [] }
      const response = await apiClient.get('/api/auth/my-project-access')
      return response.data as { access: Array<{ project_id: number; role_in_project: string }> }
    },
    enabled: !!currentUser?.id && currentUser.role !== 'system_admin',
  })

  // Загрузка всех permissions для текущего пользователя и проекта
  const { data: permissionsData } = useQuery({
    queryKey: ['my-permissions', currentUser?.id, projectId],
    queryFn: async () => {
      if (!currentUser?.id || !projectId) return { permissions: [] }
      const response = await apiClient.get('/api/auth/my-permissions', {
        params: { project_id: Number(projectId) },
      })
      return response.data as { permissions: PermissionRow[] }
    },
    enabled: !!currentUser?.id && !!projectId && currentUser.role !== 'system_admin',
  })

  const allPermissions: PermissionRow[] = permissionsData?.permissions ?? []

  // Helper функция для получения эффективного права на ресурс (с учётом наследования)
  const getEffectivePermission = (
    resourceType: 'electrical_panel' | 'schema',
    resourceId: number | null,
  ): string | null => {
    if (currentUser?.role === 'system_admin') {
      return 'manage'
    }

    if (!projectId || !currentUser?.id) return null

    // Проверяем явное permission
    const explicit = allPermissions.find(
      (p) =>
        p.project_id === Number(projectId) &&
        p.resource_type === resourceType &&
        p.resource_id === (resourceId ? String(resourceId) : null) &&
        p.permission !== 'deny',
    )
    if (explicit) {
      return explicit.permission
    }

    // Проверяем наследование от роли проекта
    const access = projectAccessData?.access?.find(
      (a) => a.project_id === Number(projectId),
    )
    if (access && access.role_in_project) {
      const roleMap: Record<string, string> = {
        viewer: 'view',
        editor: 'edit',
        project_admin: 'manage',
      }
      return roleMap[access.role_in_project] || null
    }

    return null
  }

  // Helper функция для проверки, может ли пользователь редактировать конкретный щит
  const canEditPanel = (panelId: number): boolean => {
    if (currentUser?.role === 'system_admin') return true
    const perm = getEffectivePermission('electrical_panel', panelId)
    return perm === 'edit' || perm === 'manage'
  }

  // Helper функция для проверки, может ли пользователь управлять конкретным щитом
  const canManagePanel = (panelId: number): boolean => {
    if (currentUser?.role === 'system_admin') return true
    const perm = getEffectivePermission('electrical_panel', panelId)
    return perm === 'manage'
  }

  // Определяем, может ли пользователь редактировать проект (для создания новых щитов)
  const canEdit = currentUser?.role === 'system_admin' || 
    (projectAccessData?.access?.some(
      (access) => access.project_id === Number(projectId) && 
      ['editor', 'project_admin'].includes(access.role_in_project)
    ) ?? false)

  const { data: panels, isLoading: panelsLoading } = useQuery({
    queryKey: ['electrical-panels', projectId],
    queryFn: () => getElectricalPanels(Number(projectId)),
    enabled: !!projectId,
  })

  const createPanelMutation = useMutation({
    mutationFn: createElectricalPanel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['electrical-panels', projectId] })
      handleCloseModal()
    },
  })

  const updatePanelMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<ElectricalPanel> }) => 
      updateElectricalPanel(id, {
        proj_id: data.proj_id || '',
        name: data.name || '',
        description: data.description,
        location: data.location
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['electrical-panels', projectId] })
      handleCloseModal()
    },
  })

  const deletePanelMutation = useMutation({
    mutationFn: deleteElectricalPanel,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['electrical-panels', projectId] })
    },
  })



  const handleCreatePanel = async (panelData: { proj_id: string; name: string; description: string; location: string }) => {
    if (!projectId) return
    
    try {
      if (editingPanel) {
        await updatePanelMutation.mutateAsync({
          id: editingPanel.id,
          data: { ...panelData, obj_id: Number(projectId) }
        })
      } else {
        await createPanelMutation.mutateAsync({
          obj_id: Number(projectId),
          ...panelData,
        })
      }
    } catch (error) {
      console.error('Failed to save panel:', error)
    }
  }

  const handleEditPanel = (panel: ElectricalPanel) => {
    setEditingPanel(panel)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingPanel(undefined)
  }

  const handleDeletePanel = async (panelId: number) => {
    if (confirm('Вы уверены, что хотите удалить этот электрический щит?')) {
      try {
        await deletePanelMutation.mutateAsync(panelId)
        setOpenMenuId(null)
      } catch (error) {
        console.error('Failed to delete panel:', error)
      }
    }
  }

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      Object.keys(menuRefs.current).forEach((panelIdStr) => {
        const panelId = Number(panelIdStr)
        const menuRef = menuRefs.current[panelId]
        const buttonRef = buttonRefs.current[panelId]
        if (
          menuRef &&
          !menuRef.contains(target) &&
          buttonRef &&
          !buttonRef.contains(target) &&
          openMenuId === panelId
        ) {
          setOpenMenuId(null)
        }
      })
    }

    if (openMenuId !== null) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [openMenuId])

  if (panelsLoading) {
    return <div className="text-center py-8">Loading electrical panels...</div>
  }

  const panelsList = panels || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">⚡ Электрические щиты</h2>
          <p className="text-gray-600 mt-1">Управление электрическими щитами и их схемами</p>
        </div>
        {canEdit && (
          <button
            onClick={() => {
              setEditingPanel(undefined)
              setIsModalOpen(true)
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Добавить щит
          </button>
        )}
      </div>

      {/* Panels Grid */}
      {panelsList.length > 0 ? (
        <div className="grid gap-6">
          {panelsList.map((panel) => (
            <div key={panel.id} id={`panel-${panel.id}`} className="bg-white rounded-lg shadow-md border border-gray-200">
              {/* Panel Header */}
              <div className="border-b border-gray-200 p-4">
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 flex-wrap">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium whitespace-nowrap">
                        {panel.proj_id}
                      </span>
                      <h3 className="text-xl font-semibold text-gray-900 break-words">{panel.name}</h3>
                    </div>
                    {panel.description && (
                      <p className="text-gray-600 mt-1 break-words">{panel.description}</p>
                    )}
                    {panel.location && (
                      <p className="text-gray-500 text-sm mt-1">📍 {panel.location}</p>
                    )}
                  </div>
                  <div className="relative flex-shrink-0">
                    {/* Menu Button */}
                    <button
                      ref={(el) => { buttonRefs.current[panel.id] = el }}
                      onClick={(e) => {
                        e.stopPropagation()
                        setOpenMenuId(openMenuId === panel.id ? null : panel.id)
                      }}
                      className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
                      title="Меню щита"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                        />
                      </svg>
                    </button>

                    {/* Dropdown Menu */}
                    {openMenuId === panel.id && (
                      <div
                        ref={(el) => { menuRefs.current[panel.id] = el }}
                        className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
                      >
                        <div className="py-2">
                          <Link
                            to={`/projects/${projectId}/prints`}
                            onClick={() => setOpenMenuId(null)}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                          >
                            🖨️ Печать схем
                          </Link>
                          <Link
                            to={`/projects/${projectId}/pdf-files`}
                            onClick={() => setOpenMenuId(null)}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                          >
                            📁 PDF файлы
                          </Link>
                          {canEditPanel(panel.id) && (
                            <>
                              <div className="border-t border-gray-200 my-1"></div>
                              <button
                                onClick={() => {
                                  handleEditPanel(panel)
                                  setOpenMenuId(null)
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                              >
                                ✏️ Редактировать
                              </button>
                            </>
                          )}
                          {canManagePanel(panel.id) && (
                            <>
                              <div className="border-t border-gray-200 my-1"></div>
                              <button
                                onClick={() => {
                                  handleDeletePanel(panel.id)
                                  setOpenMenuId(null)
                                }}
                                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                              >
                                🗑️ Удалить
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

                              {/* Schemas */}
                <div className="p-4 border-b border-gray-200">
                  <PanelSchemas 
                    projectId={projectId!} 
                    panelId={panel.id} 
                    onCreateSchema={() => {
                      setSelectedPanelId(panel.id);
                      setIsCreateSchemaModalOpen(true);
                    }}
                    onEditSchema={(schema) => {
                      setSelectedPanelId(panel.id)
                      setEditingSchema(schema)
                      setIsEditSchemaModalOpen(true)
                    }}
                    canEdit={canEditPanel(panel.id)}
                    canManage={canManagePanel(panel.id)}
                  />
                </div>

                {/* Panel Management */}
                <div className="p-4">
                  <h4 className="text-lg font-medium text-gray-800 mb-3">Управление щитом</h4>
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
                    <div className="border border-orange-200 rounded p-3 bg-orange-50">
                      <h5 className="font-medium text-orange-900 mb-1 text-sm">🔧 Устройства</h5>
                      <p className="text-orange-700 text-xs mb-2">Спецификации устройств щита</p>
                      <Link 
                        to={`/projects/${projectId}/panels/${panel.id}/devices`}
                        className="bg-orange-600 text-white px-2 py-1 rounded text-xs hover:bg-orange-700 inline-block"
                      >
                        Управление
                      </Link>
                    </div>

                    <div className="border border-blue-200 rounded p-3 bg-blue-50">
                      <h5 className="font-medium text-blue-900 mb-1 text-sm">📡 Кабели</h5>
                      <p className="text-blue-700 text-xs mb-2">Спецификации кабелей щита</p>
                      <Link 
                        to={`/projects/${projectId}/panels/${panel.id}/cables`}
                        className="bg-blue-600 text-white px-2 py-1 rounded text-xs hover:bg-blue-700 inline-block"
                      >
                        Управление
                      </Link>
                    </div>

                    <div className="border border-yellow-200 rounded p-3 bg-yellow-50">
                      <h5 className="font-medium text-yellow-900 mb-1 text-sm">🔗 Блоки клеммников</h5>
                      <p className="text-yellow-700 text-xs mb-2">Блоки клеммных терминалов</p>
                      <Link 
                        to={`/projects/${projectId}/panels/${panel.id}/terminal-blocks`}
                        className="bg-yellow-600 text-white px-2 py-1 rounded text-xs hover:bg-yellow-700 inline-block"
                      >
                        Управление
                      </Link>
                    </div>
                    
                    <div className="border border-green-200 rounded p-3 bg-green-50">
                      <h5 className="font-medium text-green-900 mb-1 text-sm">🔌 Экз. кабелей</h5>
                      <p className="text-green-700 text-xs mb-2">Экземпляры кабелей щита</p>
                      <Link 
                        to={`/projects/${projectId}/panels/${panel.id}/cable-instances`}
                        className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700 inline-block"
                      >
                        Управление
                      </Link>
                    </div>

                    <div className="border border-purple-200 rounded p-3 bg-purple-50">
                      <h5 className="font-medium text-purple-900 mb-1 text-sm">⚡ Экз. устройств</h5>
                      <p className="text-purple-700 text-xs mb-2">Экземпляры устройств щита</p>
                      <Link 
                        to={`/projects/${projectId}/panels/${panel.id}/device-instances`}
                        className="bg-purple-600 text-white px-2 py-1 rounded text-xs hover:bg-purple-700 inline-block"
                      >
                        Управление
                      </Link>
                    </div>
                  </div>
                </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500 bg-white rounded-lg shadow-md">
          <p className="mb-4">Нет электрических щитов в проекте</p>
          {canEdit && (
            <button
              onClick={() => {
                setEditingPanel(undefined)
                setIsModalOpen(true)
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Создать первый щит
            </button>
          )}
        </div>
      )}

      {/* Panel Modal */}
      <ElectricalPanelModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleCreatePanel}
        panel={editingPanel}
        isLoading={createPanelMutation.isPending || updatePanelMutation.isPending}
      />

      {/* Create Schema Modal */}
      <CreateSchemaModal
        isOpen={isCreateSchemaModalOpen}
        onClose={() => {
          setIsCreateSchemaModalOpen(false)
          setSelectedPanelId(null)
        }}
        projectId={Number(projectId!)}
        panelId={selectedPanelId!}
      />

      <EditSchemaModal
        isOpen={isEditSchemaModalOpen}
        onClose={() => {
          setIsEditSchemaModalOpen(false)
          setEditingSchema(null)
          setSelectedPanelId(null)
        }}
        projectId={Number(projectId!)}
        panelId={selectedPanelId!}
        schema={editingSchema}
      />
    </div>
  )
} 